<template>
  <div
    v-if="open"
    class="modal fade show d-block coupon-modal"
    tabindex="-1"
    aria-modal="true"
    role="dialog"
    @click.self="$emit('close')"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content border-0 shadow-lg rounded-4">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">{{ isEdit ? $t("coupon_page.edit_chip") : $t("coupon_page.new_chip") }}</span>
            <h5 class="modal-title fw-semibold mt-3 mb-1">
              {{ isEdit ? $t("coupon_page.update_title") : $t("coupon_page.create_title") }}
            </h5>
            <p class="text-muted mb-0 modal-copy">
              {{ $t("coupon_page.modal_description") }}
            </p>
          </div>
          <button type="button" class="btn-close shadow-none" :aria-label="$t('common.close')" @click="$emit('close')"></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <div class="coupon-tip-card mb-4">
            <div class="tip-icon">
              <i class="ri-coupon-3-line"></i>
            </div>
            <div>
              <h6 class="mb-1">
                {{ isEdit ? $t("coupon_page.edit_tip_title") : $t("coupon_page.create_tip_title") }}
              </h6>
              <p class="mb-0 text-muted">
                {{ $t("coupon_page.tip_description") }}
              </p>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("coupon_page.code") }}</label>
                <span class="field-caption">{{ $t("coupon_page.code_caption") }}</span>
              </div>
              <div class="reference-input-wrap">
                <input
                  :value="form.code"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': errors.code }"
                  :placeholder="$t('coupon_page.code_placeholder')"
                  @input="$emit('update-field', 'code', ($event.target as HTMLInputElement).value)"
                />
                <button
                  v-if="!isEdit"
                  type="button"
                  class="btn btn-light generate-code-btn"
                  :disabled="loading"
                  @click="$emit('generate-code')"
                >
                  <i class="ri-refresh-line me-1"></i>
                  {{ $t("coupon_page.generate_code") }}
                </button>
              </div>
              <div v-if="errors.code" class="invalid-feedback d-block">{{ errors.code[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("coupon_page.name") }}</label>
                <span class="field-caption">{{ $t("coupon_page.name_caption") }}</span>
              </div>
              <input
                :value="form.name"
                type="text"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.name }"
                :placeholder="$t('coupon_page.name_placeholder')"
                @input="$emit('update-field', 'name', ($event.target as HTMLInputElement).value)"
              />
              <div v-if="errors.name" class="invalid-feedback d-block">{{ errors.name[0] }}</div>
            </div>

            <div class="col-md-4">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("coupon_page.discount_type") }}</label>
                <span class="field-caption">{{ $t("coupon_page.discount_type_caption") }}</span>
              </div>
              <select
                :value="form.type"
                class="form-select modal-input"
                @change="$emit('update-field', 'type', ($event.target as HTMLSelectElement).value)"
              >
                <option value="fixed">{{ $t("coupon_page.type_fixed") }}</option>
                <option value="percentage">{{ $t("coupon_page.type_percentage") }}</option>
              </select>
            </div>

            <div class="col-md-4">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("coupon_page.discount_value") }}</label>
                <span class="field-caption">{{ $t("coupon_page.discount_value_caption") }}</span>
              </div>
              <input
                :value="form.discount"
                type="number"
                min="0"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.discount }"
                :placeholder="$t('coupon_page.discount_value_placeholder')"
                @input="$emit('update-field', 'discount', Number(($event.target as HTMLInputElement).value || 0))"
              />
              <div v-if="errors.discount" class="invalid-feedback d-block">{{ errors.discount[0] }}</div>
            </div>

            <div class="col-md-4">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("coupon_page.usage_limit") }}</label>
                <span class="field-caption">{{ $t("coupon_page.usage_limit_caption") }}</span>
              </div>
              <input
                :value="form.limit"
                type="number"
                min="0"
                class="form-control modal-input"
                :placeholder="$t('coupon_page.usage_limit_placeholder')"
                @input="$emit('update-field', 'limit', Number(($event.target as HTMLInputElement).value || 0))"
              />
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("coupon_page.start_date") }}</label>
                <span class="field-caption">{{ $t("coupon_page.start_date_caption") }}</span>
              </div>
              <input
                :value="form.start_date"
                type="date"
                class="form-control modal-input"
                @input="$emit('update-field', 'start_date', ($event.target as HTMLInputElement).value)"
              />
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("coupon_page.end_date") }}</label>
                <span class="field-caption">{{ $t("coupon_page.end_date_caption") }}</span>
              </div>
              <input
                :value="form.end_date"
                type="date"
                class="form-control modal-input"
                @input="$emit('update-field', 'end_date', ($event.target as HTMLInputElement).value)"
              />
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("coupon_page.status") }}</label>
                <span class="field-caption">{{ $t("coupon_page.status_caption") }}</span>
              </div>
              <select
                :value="Number(form.status ?? 1)"
                class="form-select modal-input"
                @change="$emit('update-field', 'status', Number(($event.target as HTMLSelectElement).value))"
              >
                <option :value="1">{{ $t("coupon_page.status_active") }}</option>
                <option :value="0">{{ $t("coupon_page.status_inactive") }}</option>
              </select>
            </div>

            <div class="col-12">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("coupon_page.description") }}</label>
                <span class="field-caption">{{ $t("coupon_page.description_caption") }}</span>
              </div>
              <textarea
                :value="form.description"
                rows="4"
                class="form-control modal-input modal-textarea"
                :placeholder="$t('coupon_page.description_placeholder')"
                @input="$emit('update-field', 'description', ($event.target as HTMLTextAreaElement).value)"
              ></textarea>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
            <button type="button" class="btn btn-light px-4" @click="$emit('close')">
              {{ $t("common.cancel") }}
            </button>
            <button type="button" class="btn style-one px-4 h-55" :disabled="loading" @click="$emit('save')">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ loading ? $t("coupon_page.saving") : isEdit ? $t("coupon_page.update_action") : $t("coupon_page.create_action") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { Coupon } from "@/services/coupon";

export default defineComponent({
  name: "CreateCouponModal",
  emits: ["close", "save", "generate-code", "update-field"],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object as PropType<Partial<Coupon>>,
      required: true,
    },
    errors: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
});
</script>

<style scoped>
.coupon-modal {
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
  font-size: 13px;
}

.coupon-tip-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 18px;
  border: 1px solid #e6ebfb;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
}

.tip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: #eef2ff;
  color: #5d5fef;
  font-size: 20px;
}

.modal-copy {
  font-size: 14px;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  color: #6b7280;
  font-size: 12px;
}

.reference-input-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: stretch;
}

.modal-input {
  min-height: 52px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #fff;
  color: #0f172a;
  box-shadow: none;
  padding: 14px 18px;
  font-size: 14px;
}

.modal-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.modal-textarea {
  min-height: 132px;
  resize: vertical;
  padding-top: 16px;
}

.generate-code-btn {
  min-height: 52px;
  padding: 0 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #f8faff;
  font-weight: 600;
  white-space: nowrap;
}
</style>
